import { TreeSelect } from 'primereact/treeselect';
import React, { forwardRef, useEffect, useMemo } from 'react'
import "../Styles/pages/payments.scss"
import { useSelector } from 'react-redux'
import {
    TreeSelectLabelTooltip,
    createTreeSelectNodeTemplate,
    createTreeSelectValueTemplate,
    truncateLabel,
} from './TreeSelectLabelHelpers';


export const TreeSelectComponent = forwardRef((props, ref) => {
    const { state, setState, placeholder, className, loadingFn, id, allowParentSelection = false,
        showClear = false, showOneOffOnly = false, subscriptionPlan = null, showRecurring = false, reportsScreen = false,
        showValueItemInfo = true, excludePlanId = null } = props;

    const planGroupsGraphedActive = useSelector((state) => state.planGroups.planGroupsGraphedActive);
    const planGroupsGraphedAll = useSelector((state) => state.planGroups.planGroupsGraphedAll);

    const treeStructure = useMemo(() => {
        const graphedGroupToUse = reportsScreen
            ? (planGroupsGraphedAll || planGroupsGraphedActive)
            : planGroupsGraphedActive
        if (!graphedGroupToUse?.groups) return [];

        return graphedGroupToUse.groups.map((groupItem, groupIndex) => {
            const { group, plans } = groupItem;

            const nonArchivedPlans = (plans?.filter(plan => !plan.status?.includes("ARCHIVED")) || []);

            const oneOffPlansNotArchived =
                plans?.filter(plan => plan.billingPeriod?.includes("ONCE")
                    && ["FIXED", "FLEXIBLE"].includes(plan?.policy) && !plan.status?.includes("ARCHIVED")) || [];

            const recurringPlansNotArchived =
                plans?.filter(plan => ["FIXED", "FLEXIBLE"].includes(plan?.policy) && !plan.status?.includes("ARCHIVED")) || [];

            const excludeId = excludePlanId != null ? String(excludePlanId) : null;
            const childrenGroup = (showOneOffOnly
                ? oneOffPlansNotArchived
                : showRecurring ? recurringPlansNotArchived : reportsScreen ? (plans || []) : nonArchivedPlans)
                .filter((plan) => !excludeId || String(plan?.id) !== excludeId);

            const groupName = group?.name ?? '';
            const groupKey = group?.id != null ? String(group.id) : String(groupIndex);
            return {
                key: groupKey,
                label: truncateLabel(groupName, 28),
                fullLabel: groupName,
                nodeId: group?.id,
                data: group,
                isParent: true,
                selectable: allowParentSelection,
                children: childrenGroup.map((plan, planIndex) => {
                    const planName = plan?.name ?? '';
                    return {
                        key: `${groupIndex}-${planIndex}`,
                        label: truncateLabel(planName, 30),
                        fullLabel: planName,
                        nodeId: plan?.id,
                        data: plan,
                        isParent: false,
                        selectable: true,
                    };
                }),
            };
        }).filter(groupNode => groupNode.children.length > 0);
        
    }, [planGroupsGraphedActive,planGroupsGraphedAll, allowParentSelection, showOneOffOnly, showRecurring, reportsScreen, excludePlanId]);

    const selectedNodeData = useMemo(() => {
        const selectedKey = state.treePlanSelectedKey;
        if (selectedKey == null || selectedKey === '') return null;

        for (const group of treeStructure) {
            if (String(group.key) === String(selectedKey) || String(group.nodeId) === String(selectedKey)) {
                return group;
            }

            const child = group.children?.find(
                c => String(c.key) === String(selectedKey) || String(c.nodeId) === String(selectedKey)
            );
            if (child) return child;
        }
        return null;
    }, [treeStructure, state.treePlanSelectedKey]);

    useEffect(() => {
        if (selectedNodeData) {
            if (!String(state.treePlanSelectedKey).includes("-")) {
                setState(prev => ({
                    ...prev,
                    treeProductSelected: selectedNodeData.data,
                    treePlanSelected: null,
                }));
            }
            else {
                setState(prev => ({
                    ...prev,
                    treePlanSelected: selectedNodeData.data,
                    treeProductSelected: null,
                }));
            }
        } else if (state.treePlanSelectedKey == null || state.treePlanSelectedKey === '') {
            setState(prev => ({
                ...prev,
                treeProductSelected: null,
                treePlanSelected: null,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedNodeData, state.treePlanSelectedKey]);

    const handleChange = (e) => {
        const selectedKey = e.value;

        const selectedNode = treeStructure
            .flatMap(g => [g, ...(g.children || [])])
            .find(n => n.key === selectedKey || String(n.key) === String(selectedKey) || String(n.nodeId) === String(selectedKey));

        if (selectedNode?.isParent && !allowParentSelection) {
            return;
        }

        setState(prev => ({
            ...prev,
            treePlanSelectedKey: selectedKey,
        }));
        if (typeof loadingFn === 'function') {
            loadingFn();
        }
    };

    return (
        <>
            <TreeSelectLabelTooltip />
            <TreeSelect
                ref={ref}
                id={id}
                value={state.treePlanSelectedKey == null || state.treePlanSelectedKey === '' ? null : String(state.treePlanSelectedKey)}
                options={treeStructure}
                onChange={handleChange}
                placeholder={placeholder}
                filter
                filterBy="fullLabel,label"
                showClear={showClear}
                selectionMode="single"
                style={{ height: "3rem" }}
                className={className}
                disabled={subscriptionPlan}
                nodeTemplate={createTreeSelectNodeTemplate(true)}
                valueTemplate={createTreeSelectValueTemplate(showValueItemInfo)}
            />
        </>
    );
});